// src/pages/Returns/Returns.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, doc, updateDoc, addDoc, serverTimestamp, getDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const styles = {
    container: { 
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    button: { 
        padding: '12px 24px', 
        border: 'none', 
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)', 
        color: 'white', 
        borderRadius: '12px', 
        cursor: 'pointer', 
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s ease-in-out'
    },
    box: { 
        background: 'white', 
        padding: '32px', 
        borderRadius: '16px', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0'
    },
    formGroup: { marginBottom: '20px' },
    label: { 
        display: 'block', 
        marginBottom: '8px', 
        fontWeight: '600', 
        color: '#374151',
        fontSize: '14px'
    },
    input: { 
        width: '100%', 
        padding: '12px 16px', 
        fontSize: '14px', 
        border: '1px solid #e2e8f0', 
        borderRadius: '8px', 
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        background: 'white',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    tableContainer: {
        maxHeight: '60vh',
        overflowY: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        marginTop: '20px'
    },
    table: { 
        width: '100%', 
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden'
    },
    th: { 
        background: '#f8fafc', 
        padding: '16px 20px', 
        textAlign: 'left', 
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        position: 'sticky',
        top: 0
    },
    td: { 
        padding: '16px 20px', 
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    trHover: { 
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    trHoverActive: {
        background: '#f8fafc'
    },
    modalOverlay: { 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        background: 'rgba(0,0,0,0.5)', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        zIndex: 100 
    },
    modalContent: { 
        background: 'white', 
        padding: '32px', 
        borderRadius: '16px', 
        width: '90%', 
        maxWidth: '800px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
        maxHeight: '90vh',
        overflowY: 'auto'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyIcon: {
        fontSize: '48px',
        marginBottom: '16px'
    },
    emptyText: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    emptySubtext: {
        fontSize: '14px',
        color: '#64748b'
    }
};

function AddReturnModal({ onClose, onSave, customers, products }) {
    const [formData, setFormData] = useState({
        customerId: '',
        productId: '',
        quantity: 1,
        reason: '',
        description: '',
        currency: 'TRY'
    });
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productPrice, setProductPrice] = useState(0);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Update related data when selections change
        if (name === 'customerId') {
            const customer = customers.find(c => c.id === value);
            setSelectedCustomer(customer || null);
        } else if (name === 'productId') {
            const product = products.find(p => p.id === value);
            setSelectedProduct(product || null);
            setProductPrice(product?.salePrice || 0);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.customerId || !formData.productId || !formData.quantity) {
            alert('Lütfen tüm alanları doldurun.');
            return;
        }
        
        // Validate quantity against available stock if needed
        if (parseInt(formData.quantity) <= 0) {
            alert('Geçerli bir miktar girin.');
            return;
        }
        
        const returnData = {
            ...formData,
            customerName: selectedCustomer?.name || '',
            productName: selectedProduct?.name || '',
            unitPrice: productPrice,
            totalPrice: productPrice * formData.quantity,
            status: 'completed',
            createdAt: serverTimestamp()
        };
        
        onSave(returnData);
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={{...styles.modalContent, maxWidth: '600px'}} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Ürün İadesi</h2>
                    <button 
                        onClick={onClose}
                        style={{ 
                            background: 'none', 
                            border: 'none', 
                            fontSize: '24px', 
                            cursor: 'pointer',
                            color: '#94a3b8'
                        }}
                    >
                        ×
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Müşteri *</label>
                        <select
                            name="customerId"
                            value={formData.customerId}
                            onChange={handleChange}
                            required
                            style={styles.select}
                        >
                            <option value="">Müşteri Seçin</option>
                            {customers.map(customer => (
                                <option key={customer.id} value={customer.id}>
                                    {customer.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Para Birimi *</label>
                        <select
                            name="currency"
                            value={formData.currency}
                            onChange={handleChange}
                            required
                            style={styles.select}
                        >
                            <option value="TRY">Türk Lirası (₺)</option>
                            <option value="USD">Amerikan Doları ($)</option>
                        </select>
                    </div>
                    
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Ürün *</label>
                        <select
                            name="productId"
                            value={formData.productId}
                            onChange={handleChange}
                            required
                            style={styles.select}
                        >
                            <option value="">Ürün Seçin</option>
                            {products.map(product => (
                                <option key={product.id} value={product.id}>
                                    {product.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Miktar *</label>
                        <input
                            type="number"
                            name="quantity"
                            value={formData.quantity}
                            onChange={handleChange}
                            required
                            min="1"
                            style={styles.input}
                        />
                    </div>
                    
                    <div style={styles.formGroup}>
                        <label style={styles.label}>İade Nedeni</label>
                        <select
                            name="reason"
                            value={formData.reason}
                            onChange={handleChange}
                            style={styles.select}
                        >
                            <option value="">Sebep Seçin</option>
                            <option value="hatali_urun">Hatalı Ürün</option>
                            <option value="kargo_hasari">Kargo Hasarı</option>
                            <option value="degistirme">Değiştirme</option>
                            <option value="istenmeyen_urun">İstenmeyen Ürün</option>
                            <option value="diger">Diğer</option>
                        </select>
                    </div>
                    
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Açıklama</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            style={{...styles.input, minHeight: '100px', resize: 'vertical'}}
                            placeholder="İade detaylarını girin..."
                        />
                    </div>
                    
                    {selectedProduct && (
                        <div style={{ 
                            background: '#f8fafc', 
                            padding: '15px', 
                            borderRadius: '8px', 
                            marginBottom: '20px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontWeight: '600' }}>
                                        Toplam: {(productPrice * formData.quantity || 0).toFixed(2)} {formData.currency === 'USD' ? '$' : '₺'}
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#64748b' }}>
                                        Birim Fiyat: {(productPrice || 0).toFixed(2)} {formData.currency === 'USD' ? '$' : '₺'}
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#64748b' }}>
                                        Stok: {selectedProduct.quantity || 0} {selectedProduct.unit || 'Adet'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div style={styles.modalActions}>
                        <button 
                            type="button" 
                            onClick={onClose}
                            style={{ 
                                ...styles.button, 
                                background: '#f1f5f9', 
                                color: '#374151',
                                border: '1px solid #e2e8f0'
                            }}
                        >
                            İptal
                        </button>
                        <button 
                            type="submit" 
                            style={{ ...styles.button, background: 'linear-gradient(135deg, #10b981, #059669)' }}
                        >
                            İadeyi Kaydet
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Returns() {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [returns, setReturns] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);

    // Fetch data
    useEffect(() => {
        if (!userData?.companyId) return;
        
        const companyId = userData.companyId;
        
        // Fetch returns
        const unsubReturns = onSnapshot(
            query(collection(db, 'companies', companyId, 'returns')), 
            snap => setReturns(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
        
        // Fetch customers
        const unsubCustomers = onSnapshot(
            query(collection(db, 'companies', companyId, 'customers')), 
            snap => setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
        
        // Fetch products
        const unsubProducts = onSnapshot(
            query(collection(db, 'companies', companyId, 'products')), 
            snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        return () => { 
            unsubReturns(); 
            unsubCustomers(); 
            unsubProducts(); 
        };
    }, [userData]);

    const handleAddReturn = async (returnData) => {
        try {
            const companyId = userData.companyId;
            
            // Use batch write to ensure atomicity
            const batch = writeBatch(db);
            
            // 1. Add the return record
            const returnRef = doc(collection(db, 'companies', companyId, 'returns'));
            batch.set(returnRef, returnData);
            
            // 2. Update product stock (add returned quantity)
            const productRef = doc(db, 'companies', companyId, 'products', returnData.productId);
            const productDoc = await getDoc(productRef);
            if (productDoc.exists()) {
                const currentQuantity = productDoc.data().quantity || 0;
                batch.update(productRef, {
                    quantity: currentQuantity + parseInt(returnData.quantity)
                });
                
                // Add stock movement record for the return
                const movementRef = doc(collection(productRef, 'movements'));
                batch.set(movementRef, {
                    date: new Date(),
                    type: 'İade',
                    quantity: `+${returnData.quantity} ${productDoc.data().unit || 'Adet'}`,
                    description: `Müşteri: ${returnData.customerName}, Neden: ${returnData.reason || 'Belirtilmemiş'}`
                });
            }
            
            // 3. Update customer balance (subtract return amount)
            const customerRef = doc(db, 'companies', companyId, 'customers', returnData.customerId);
            const customerDoc = await getDoc(customerRef);
            if (customerDoc.exists()) {
                const customerData = customerDoc.data();
                const currentBalance = customerData.balance || 0;
                const customerCurrency = customerData.currency || 'TRY';

                let balanceImpact = returnData.totalPrice;

                // Handle currency conversion
                if (returnData.currency !== customerCurrency) {
                    // Fetch company info for exchange rates
                    const companyDoc = await getDoc(doc(db, 'companies', companyId));
                    const companyData = companyDoc.data();
                    const exchangeRates = companyData?.currencySettings || { USD: 33 };
                    const usdRate = exchangeRates.USD || exchangeRates.rate || 33;

                    if (returnData.currency === 'USD' && customerCurrency === 'TRY') {
                        balanceImpact = returnData.totalPrice * usdRate;
                    } else if (returnData.currency === 'TRY' && customerCurrency === 'USD') {
                        balanceImpact = returnData.totalPrice / usdRate;
                    }
                }

                const newBalance = currentBalance + balanceImpact;
                batch.update(customerRef, {
                    balance: newBalance
                });

                // Bakiye geçmişi kaydı oluştur (mutabakat raporları için)
                const balanceHistoryRef = doc(collection(customerRef, 'balanceHistory'));
                batch.set(balanceHistoryRef, {
                    date: serverTimestamp(),
                    type: 'İade',
                    description: `İade: ${returnData.productName || 'Ürün iadesi'} (${returnData.quantity} adet)`,
                    amount: balanceImpact,
                    previousBalance: currentBalance,
                    newBalance: newBalance,
                    returnId: returnRef.id
                });
            }
            
            // 4. Add transaction record for the return
            const transactionRef = doc(collection(db, 'companies', companyId, 'transactions'));
            batch.set(transactionRef, {
                customerId: returnData.customerId,
                customerName: returnData.customerName,
                relatedPartyId: returnData.customerId,
                type: 'return',
                amount: returnData.totalPrice,
                currency: returnData.currency || 'TRY',
                description: `İade: ${returnData.productName} (${returnData.quantity} adet)`,
                date: serverTimestamp(),
                relatedDocumentId: returnRef.id,
                status: 'completed'
            });
            
            // Commit all changes
            await batch.commit();
            
            setShowAddModal(false);
            alert('İade işlemi başarıyla kaydedildi!');
        } catch (error) {
            console.error("İade kaydedilirken hata oluştu:", error);
            alert('İade kaydedilirken bir hata oluştu: ' + error.message);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Ürün İadeleri</h1>
                    <p style={{ color: '#64748b', marginTop: '4px' }}>
                        Müşteri iadelerini yönetin
                    </p>
                </div>
                <div style={styles.headerActions}>
                    <button 
                        onClick={() => navigate('/iadeler/fatura')}
                        style={{...styles.button, background: 'linear-gradient(135deg, #ef4444, #dc2626)'}}
                    >
                        🧾 İade Faturası Kes
                    </button>
                    <button 
                        onClick={() => setShowAddModal(true)}
                        style={styles.button}
                    >
                        + Yeni İade
                    </button>
                </div>
            </div>

            <div style={styles.box}>
                {returns.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={styles.emptyIcon}>📦</div>
                        <h3 style={styles.emptyText}>İade Kaydı Bulunamadı</h3>
                        <p style={styles.emptySubtext}>
                            Henüz hiç ürün iadesi kaydı oluşturulmamış.
                        </p>
                        <button 
                            onClick={() => setShowAddModal(true)}
                            style={{...styles.button, marginTop: '20px'}}
                        >
                            İlk İadeyi Kaydet
                        </button>
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Müşteri</th>
                                    <th style={styles.th}>Ürün</th>
                                    <th style={styles.th}>Miktar</th>
                                    <th style={styles.th}>Tutar</th>
                                    <th style={styles.th}>Neden</th>
                                    <th style={styles.th}>Tarih</th>
                                    <th style={styles.th}>İade Faturası</th>
                                    <th style={styles.th}>İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {returns.map(returnItem => (
                                    <tr 
                                        key={returnItem.id} 
                                        style={{...styles.trHover, ...(hoveredRow === returnItem.id ? styles.trHoverActive : {})}} 
                                        onMouseEnter={() => setHoveredRow(returnItem.id)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                    >
                                        <td style={styles.td}>
                                            <div style={{ fontWeight: '600' }}>{returnItem.customerName}</div>
                                        </td>
                                        <td style={styles.td}>{returnItem.productName}</td>
                                        <td style={styles.td}>{returnItem.quantity} adet</td>
                                        <td style={styles.td}>{(returnItem.totalPrice || 0).toFixed(2)} {returnItem.currency === 'USD' ? '$' : '₺'}</td>
                                        <td style={styles.td}>
                                            {returnItem.reason === 'hatali_urun' && 'Hatalı Ürün'}
                                            {returnItem.reason === 'kargo_hasari' && 'Kargo Hasarı'}
                                            {returnItem.reason === 'degistirme' && 'Değiştirme'}
                                            {returnItem.reason === 'istenmeyen_urun' && 'İstenmeyen Ürün'}
                                            {returnItem.reason === 'diger' && 'Diğer'}
                                            {!returnItem.reason && '-'}
                                        </td>
                                        <td style={styles.td}>
                                            {returnItem.createdAt?.toDate ? 
                                                returnItem.createdAt.toDate().toLocaleDateString('tr-TR') : 
                                                'Tarih yok'}
                                        </td>
                                        <td style={styles.td}>
                                            {returnItem.returnInvoiceId ? (
                                                <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>✅ Kesildi</span>
                                            ) : (
                                                <span style={{ background: '#fef3c7', color: '#92400e', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>⏳ Bekliyor</span>
                                            )}
                                        </td>
                                        <td style={styles.td}>
                                            {!returnItem.returnInvoiceId && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate('/iadeler/fatura', {
                                                            state: {
                                                                returnData: {
                                                                    customerId: returnItem.customerId,
                                                                    returns: [{
                                                                        ...returnItem,
                                                                        returnId: returnItem.id
                                                                    }]
                                                                }
                                                            }
                                                        });
                                                    }}
                                                    style={{
                                                        background: '#ef4444',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '6px 12px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    🧾 Fatura Kes
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            {showAddModal && (
                <AddReturnModal 
                    onClose={() => setShowAddModal(false)} 
                    onSave={handleAddReturn} 
                    customers={customers}
                    products={products}
                />
            )}
        </div>
    );
}

export default Returns;